import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';


function Settings({ user, setUser }) {
  const [pin, setPin] = useState('');
  const [voiceCode, setVoiceCode] = useState('');
  const [language, setLanguage] = useState(user.language || 'en');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const navigate = useNavigate();

  // Personal Phone Number Change Modal & OTP state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneStep, setPhoneStep] = useState(1); // 1 = Enter new number, 2 = Enter OTP
  const [newPhone, setNewPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneTimer, setPhoneTimer] = useState(60);
  const [canResendPhoneOtp, setCanResendPhoneOtp] = useState(false);
  const [phoneModalError, setPhoneModalError] = useState('');
  const [phoneModalSuccess, setPhoneModalSuccess] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  // CallBunker Defense Number Change Modal state
  const [showDefenseModal, setShowDefenseModal] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [selectedDefenseNumber, setSelectedDefenseNumber] = useState(null);
  const [freeChangeAvailable, setFreeChangeAvailable] = useState(true);
  const [defenseModalError, setDefenseModalError] = useState('');
  const [defenseModalSuccess, setDefenseModalSuccess] = useState('');
  const [defenseLoading, setDefenseLoading] = useState(false);
  const [searchAreaCode, setSearchAreaCode] = useState('');

  React.useEffect(() => {
    let interval = null;
    if (showPhoneModal && phoneStep === 2 && phoneTimer > 0) {
      interval = setInterval(() => {
        setPhoneTimer((prev) => prev - 1);
      }, 1000);
    } else if (phoneTimer === 0) {
      setCanResendPhoneOtp(true);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [showPhoneModal, phoneStep, phoneTimer]);

  React.useEffect(() => {
    window.onConsumablePurchaseCompleted = async (token) => {
      console.log('Consumable purchase token received from native:', token);
      setDefenseModalSuccess('Payment verified! Updating your CallBunker Defense Number...');
      setDefenseModalError('');
      if (selectedDefenseNumber) {
        try {
          const authToken = localStorage.getItem('token');
          const response = await axios.post(
            '/api/user/select-number',
            {
              selectedNumber: selectedDefenseNumber.phoneNumber,
              isPool: selectedDefenseNumber.isPool,
              purchaseToken: token,
            },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          if (response.data.success) {
            setUser((prev) => ({ ...prev, twilioNumber: response.data.twilioNumber }));
            setShowDefenseModal(false);
            setMessage('CallBunker Defense Number updated successfully!');
            setMessageType('success');
            setTimeout(() => setMessage(''), 4000);
          }
        } catch (err) {
          setDefenseModalError(err.response?.data?.message || 'Failed to update CallBunker Defense Number');
        } finally {
          setDefenseLoading(false);
        }
      }
    };

    return () => {
      delete window.onConsumablePurchaseCompleted;
    };
  }, [selectedDefenseNumber, setUser]);

  const handleOpenPhoneModal = () => {
    setNewPhone('');
    setPhoneOtp('');
    setPhoneStep(1);
    setPhoneModalError('');
    setPhoneModalSuccess('');
    setShowPhoneModal(true);
  };

  const handleSendPhoneOtp = async (e) => {
    e.preventDefault();
    setPhoneModalError('');
    setPhoneModalSuccess('');

    const phoneRegex = /^\+\d{7,15}$/;
    if (!phoneRegex.test(newPhone)) {
      setPhoneModalError('Please enter a valid phone number with country code (e.g., +1234567890)');
      return;
    }

    setPhoneLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/user/send-number-change-otp',
        { newRealNumber: newPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhoneStep(2);
      setPhoneTimer(60);
      setCanResendPhoneOtp(false);
      setPhoneModalSuccess(`Verification code sent to ${newPhone}`);
    } catch (err) {
      setPhoneModalError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleResendPhoneOtp = async () => {
    if (!canResendPhoneOtp) return;
    setPhoneModalError('');
    setPhoneModalSuccess('');
    setPhoneLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/user/send-number-change-otp',
        { newRealNumber: newPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhoneTimer(60);
      setCanResendPhoneOtp(false);
      setPhoneModalSuccess(`New verification code sent to ${newPhone}`);
    } catch (err) {
      setPhoneModalError(err.response?.data?.message || 'Failed to resend verification code');
    } finally {
      setPhoneLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async (e) => {
    e.preventDefault();
    setPhoneModalError('');
    setPhoneModalSuccess('');

    if (!phoneOtp || phoneOtp.trim().length === 0) {
      setPhoneModalError('Please enter the 6-digit verification code');
      return;
    }

    setPhoneLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/user/verify-number-change-otp',
        { code: phoneOtp.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser({ ...user, realNumber: response.data.realNumber });
      setShowPhoneModal(false);
      setMessage('Personal phone number updated successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setPhoneModalError(err.response?.data?.message || 'Verification failed. Please check the code.');
    } finally {
      setPhoneLoading(false);
    }
  };

  const fetchAvailableNumbers = async (areaCodeParam = '') => {
    setDefenseLoading(true);
    setDefenseModalError('');
    try {
      const token = localStorage.getItem('token');
      const url = areaCodeParam.trim()
        ? `/api/user/available-numbers?areaCode=${encodeURIComponent(areaCodeParam.trim())}`
        : '/api/user/available-numbers';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setAvailableNumbers(response.data.numbers || []);
        setFreeChangeAvailable(response.data.freeChangeAvailable);
        if (response.data.numbers && response.data.numbers.length > 0) {
          setSelectedDefenseNumber(response.data.numbers[0]);
        } else {
          setSelectedDefenseNumber(null);
        }
      }
    } catch (err) {
      setDefenseModalError(err.response?.data?.message || 'Failed to fetch candidate phone numbers');
    } finally {
      setDefenseLoading(false);
    }
  };

  const handleOpenDefenseModal = async () => {
    setDefenseModalError('');
    setDefenseModalSuccess('');
    setSelectedDefenseNumber(null);
    setAvailableNumbers([]);
    setSearchAreaCode('');

    if (!user.isSubscribed) {
      setMessage('Number customization is available exclusively for active subscribers. Please upgrade your subscription first.');
      setMessageType('warning');
      setTimeout(() => setMessage(''), 5000);
      return;
    }

    setShowDefenseModal(true);
    await fetchAvailableNumbers('');
  };

  const handleSearchAreaCode = async (e) => {
    e.preventDefault();
    await fetchAvailableNumbers(searchAreaCode);
  };

  const handleConfirmDefenseChange = async (e) => {
    e.preventDefault();
    if (!selectedDefenseNumber) {
      setDefenseModalError('Please select a number from the list');
      return;
    }

    setDefenseLoading(true);
    setDefenseModalError('');
    setDefenseModalSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        '/api/user/select-number',
        {
          selectedNumber: selectedDefenseNumber.phoneNumber,
          isPool: selectedDefenseNumber.isPool,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setUser({ ...user, twilioNumber: response.data.twilioNumber });
        setShowDefenseModal(false);
        setMessage('CallBunker Defense Number updated successfully!');
        setMessageType('success');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (err) {
      if (err.response?.status === 402) {
        if (window.flutter_inappwebview) {
          window.flutter_inappwebview.callHandler('purchaseConsumable', 'callbunker_number_change_2_50');
          setDefenseModalError('A fee of $2.50 is required. Opening Google Play Billing...');
        } else {
          setDefenseModalError('A fee of $2.50 is required for additional number changes. Please complete the purchase in the CallBunker Android App.');
        }
      } else {
        setDefenseModalError(err.response?.data?.message || 'Failed to update CallBunker Defense Number');
      }
    } finally {
      setDefenseLoading(false);
    }
  };

  const handleUpdatePIN = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!/^\d{4}$/.test(pin)) {
      setMessage('PIN must be exactly 4 digits');
      setMessageType('danger');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/user/pin', 
        { pin },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setUser({ ...user, pin });
      setMessage('PIN updated successfully!');
      setMessageType('success');
      setPin('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update PIN');
      setMessageType('danger');
    }
  };

  const logout = async () => {
    try {
      await axios.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem("token");
    navigate('/login');
  };

  const handleUpdateVoiceCode = async (e) => {
    e.preventDefault();
    setMessage('');

    if (voiceCode.length < 3) {
      setMessage('Voice code must be at least 3 characters');
      setMessageType('danger');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/user/voicecode', 
        { voiceCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setUser({ ...user, voiceCode });
      setMessage('Voice code updated successfully!');
      setMessageType('success');
      setVoiceCode('');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update voice code');
      setMessageType('danger');
    }
  };

  const handleUpdateLanguage = async (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      await axios.put('/api/user/language', 
        { language: newLanguage },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setUser({ ...user, language: newLanguage });
      setMessage('Language updated successfully!');
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Failed to update language');
      setMessageType('danger');
    }
  };

  const handleAuthToggle = async (method, enabled) => {
    setMessage("");

    // Prevent disabling both
    if (!enabled) {
      const otherMethod =
        method === "pin" ? "voiceCodeAuthEnabled" : "pinAuthEnabled";
      if (!user[otherMethod]) {
        setMessage("Error: You cannot disable both authentication methods.");
        setMessageType("danger");
        setTimeout(() => setMessage(""), 4000);
        return;
      }
    }

    try {
      const token = localStorage.getItem("token");
      const field =
        method === "pin" ? "pinAuthEnabled" : "voiceCodeAuthEnabled";

      await axios.put(
        "/api/user/auth-methods",
        { [field]: enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser((prev) => ({ ...prev, [field]: enabled }));
      setMessage(
        `${method === "pin" ? "PIN" : "Voice Code"} authentication ${
          enabled ? "enabled" : "disabled"
        }`
      );
      setMessageType("success");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to update settings");
      setMessageType("danger");
    }
  };

  const handleSmsProtectionToggle = async (enabled) => {
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        "/api/user/sms-protection",
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser((prev) => ({ ...prev, smsProtectionEnabled: enabled }));
      setMessage(`SMS Protection ${enabled ? "enabled" : "disabled"} successfully`);
      setMessageType("success");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      setMessage(error.response?.data?.message || "Failed to update SMS protection");
      setMessageType("danger");
    }
  };

  return (
    <Container className="mainWrapper">
      <Row className="mb-4">
        <Col>
          <h2 className="text-light fs-5">
            <i className="fas fa-cog me-2"></i>
            Settings
          </h2>
        </Col>
        <Col style={{ textAlign: "end" }}>
          <Button variant="outline-light" size="sm" onClick={logout}>
            <i className="fas fa-sign-out-alt"></i>
          </Button>
        </Col>
      </Row>

      {message && (
        <Row className="mb-3">
          <Col>
            <Alert variant={messageType}>{message}</Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Body>
              <h4>
                <i className="fas fa-lock me-2 text-primary"></i>
                Update PIN
              </h4>
              <p className="text-muted">
                Change your 4-digit authentication PIN
              </p>
              <Form onSubmit={handleUpdatePIN}>
                <Form.Group className="mb-3">
                  <Form.Label>New 4-Digit PIN</Form.Label>
                  <Form.Control
                    type="text"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="Enter new PIN"
                    maxLength={4}
                    pattern="\d{4}"
                  />
                </Form.Group>
                <Button variant="primary" type="submit">
                  <i className="fas fa-save me-2"></i>
                  Update PIN
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6} className="mb-4">
          <Card>
            <Card.Body>
              <h4>
                <i className="fas fa-microphone me-2 text-success"></i>
                Update Voice Code
              </h4>
              <p className="text-muted">
                Change your verbal authentication password
              </p>
              <Form onSubmit={handleUpdateVoiceCode}>
                <Form.Group className="mb-3">
                  <Form.Label>New Voice Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={voiceCode}
                    onChange={(e) => setVoiceCode(e.target.value)}
                    placeholder="Enter new voice code"
                  />
                </Form.Group>
                <Button variant="success" type="submit">
                  <i className="fas fa-save me-2"></i>
                  Update Voice Code
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Body>
              <h4>
                <i className="fas fa-language me-2 text-info"></i>
                Language Preference
              </h4>
              <p className="text-muted">
                Select the language for voice prompts
              </p>
              <Form.Group>
                <Form.Label>Voice Prompt Language</Form.Label>
                <Form.Select value={language} onChange={handleUpdateLanguage}>
                  <option value="en">English</option>
                  <option value="es">Spanish (Español)</option>
                  <option value="fr">French (Français)</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        
          <Col lg={6} className="mb-4">
            <Card>
              <Card.Body>
                <h4>
                  <i className="fas fa-shield-alt me-2 text-warning"></i>
                  Authentication Methods
                </h4>
                <p className="text-muted">
                  You must have at least <strong>one</strong> authentication
                  method enabled.
                </p>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="pin-auth"
                    label="PIN Authentication"
                    checked={user.pinAuthEnabled ?? true}
                    onChange={(e) => handleAuthToggle("pin", e.target.checked)}
                    disabled={(e) =>
                      !user.voiceCodeAuthEnabled && e.target.checked === false
                    }
                  />
                  <Form.Text className="text-muted">
                    Callers enter your 4-digit PIN
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="voice-auth"
                    label="Voice Code Authentication"
                    checked={user.voiceCodeAuthEnabled ?? true}
                    onChange={(e) =>
                      handleAuthToggle("voice", e.target.checked)
                    }
                    disabled={(e) =>
                      !user.pinAuthEnabled && e.target.checked === false
                    }
                  />
                  <Form.Text className="text-muted">
                    Callers say your voice code phrase
                  </Form.Text>
                </Form.Group>

                <hr />

                <Form.Group className="mb-2">
                  <Form.Check
                    type="switch"
                    id="sms-protection"
                    label="Protected Text Messaging (SMS)"
                    checked={user.smsProtectionEnabled ?? true}
                    onChange={(e) =>
                      handleSmsProtectionToggle(e.target.checked)
                    }
                  />
                  <Form.Text className="text-muted">
                    When enabled, unverified senders must reply with your PIN or passcode before their messages reach your inbox.
                  </Form.Text>
                </Form.Group>

                {message && (
                  <Alert variant={messageType} className="mt-3">
                    {message}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        
        <Col lg={6} className="mb-4">
          <Card>
            <Card.Body>
              <h4>
                <i className="fas fa-user-circle me-2 text-secondary"></i>
                Account Information
              </h4>
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td>
                      <strong>Name:</strong>
                    </td>
                    <td>{user.name}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Email:</strong>
                    </td>
                    <td>{user.email}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Personal Number:</strong>
                    </td>
                    <td className="d-flex justify-content-between align-items-center">
                      <span>{user.realNumber || "Not set"}</span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleOpenPhoneModal}
                      >
                        Change
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Defense Number:</strong>
                    </td>
                    <td className="d-flex justify-content-between align-items-center text-primary">
                      <span>{user.twilioNumber || "Assigning..."}</span>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleOpenDefenseModal}
                      >
                        Change
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Language:</strong>
                    </td>
                    <td>
                      {language === "en" && "English"}
                      {language === "es" && "Spanish"}
                      {language === "fr" && "French"}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Voice Code:</strong>
                    </td>
                    <td>{user.voiceCode || "Not set"}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>PIN:</strong>
                    </td>
                    <td>{user.pin || "Not set"}</td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Change Personal Phone Number Modal */}
      <Modal show={showPhoneModal} onHide={() => setShowPhoneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Change Personal Phone Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {phoneModalError && <Alert variant="danger">{phoneModalError}</Alert>}
          {phoneModalSuccess && <Alert variant="success">{phoneModalSuccess}</Alert>}

          {phoneStep === 1 ? (
            <Form onSubmit={handleSendPhoneOtp}>
              <Form.Group className="mb-3">
                <Form.Label>Current Number</Form.Label>
                <Form.Control type="text" value={user.realNumber || 'Not set'} disabled />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>New Personal Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="+1234567890"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">
                  Must include country code. We will send an SMS OTP verification code to this number.
                </Form.Text>
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100" disabled={phoneLoading}>
                {phoneLoading ? 'Sending Verification Code...' : 'Send Verification Code'}
              </Button>
            </Form>
          ) : (
            <Form onSubmit={handleVerifyPhoneOtp}>
              <p className="text-muted text-center mb-3">
                Enter the 6-digit code sent to <strong>{newPhone}</strong>
              </p>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="123456"
                  maxLength={6}
                  value={phoneOtp}
                  onChange={(e) => setPhoneOtp(e.target.value)}
                  required
                  style={{
                    fontSize: '22px',
                    letterSpacing: '6px',
                    textAlign: 'center',
                  }}
                />
              </Form.Group>
              <Button variant="success" type="submit" className="w-100 mb-3" disabled={phoneLoading}>
                {phoneLoading ? 'Verifying Code...' : 'Verify & Update Number'}
              </Button>
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none"
                  onClick={handleResendPhoneOtp}
                  disabled={!canResendPhoneOtp || phoneLoading}
                >
                  {canResendPhoneOtp ? 'Resend Code' : `Resend code in ${phoneTimer}s`}
                </Button>
                <Button
                  variant="link"
                  className="p-0 text-decoration-none text-secondary"
                  onClick={() => {
                    setPhoneStep(1);
                    setPhoneModalError('');
                    setPhoneModalSuccess('');
                  }}
                  disabled={phoneLoading}
                >
                  Edit Number
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* Change CallBunker Defense Number Modal */}
      <Modal show={showDefenseModal} onHide={() => setShowDefenseModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Select Your CallBunker Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {defenseModalError && <Alert variant="danger">{defenseModalError}</Alert>}
          {defenseModalSuccess && <Alert variant="success">{defenseModalSuccess}</Alert>}

          <p className="text-muted mb-3">
            {freeChangeAvailable
              ? '✨ Your first CallBunker number change is FREE! Select a new number below:'
              : 'A fee of $2.50 is required for additional number changes. Select a new number below:'}
          </p>

          {/* Area Code Search Bar */}
          <Form onSubmit={handleSearchAreaCode} className="mb-3">
            <Row className="g-2 align-items-center">
              <Col xs={8} sm={9}>
                <Form.Control
                  type="text"
                  placeholder="Search Area Code (e.g. 508, 617)"
                  value={searchAreaCode}
                  onChange={(e) => setSearchAreaCode(e.target.value)}
                  maxLength={3}
                />
              </Col>
              <Col xs={4} sm={3}>
                <Button variant="outline-primary" type="submit" className="w-100" disabled={defenseLoading}>
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {defenseLoading && availableNumbers.length === 0 ? (
            <div className="text-center py-4">
              <span className="spinner-border text-primary me-2" role="status"></span>
              Loading available numbers...
            </div>
          ) : (
            <Form onSubmit={handleConfirmDefenseChange}>
              <Form.Group className="mb-4">
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {availableNumbers.map((num, idx) => (
                    <div
                      key={idx}
                      className={`p-3 mb-2 rounded border cursor-pointer d-flex justify-content-between align-items-center ${
                        selectedDefenseNumber?.phoneNumber === num.phoneNumber
                          ? 'border-primary bg-light'
                          : 'border-secondary'
                      }`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedDefenseNumber(num)}
                    >
                      <Form.Check
                        type="radio"
                        id={`num-${idx}`}
                        name="defenseNumberGroup"
                        label={
                          <span className="fw-bold fs-6 text-dark ms-2">
                            {num.phoneNumber}
                          </span>
                        }
                        checked={selectedDefenseNumber?.phoneNumber === num.phoneNumber}
                        onChange={() => setSelectedDefenseNumber(num)}
                      />
                      {num.isPool && (
                        <span className="badge bg-success">Instant Pool</span>
                      )}
                    </div>
                  ))}
                </div>
              </Form.Group>

              <Button
                variant={freeChangeAvailable ? 'success' : 'primary'}
                type="submit"
                className="w-100 py-2 fs-6"
                disabled={defenseLoading || !selectedDefenseNumber}
              >
                {defenseLoading
                  ? 'Updating Number...'
                  : freeChangeAvailable
                  ? 'Confirm Selection (Free)'
                  : 'Confirm Selection ($2.50)'}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default Settings;
